import { DailyProductionReport } from "../../../types/mes";
import { dateLocale, t } from "../../../i18n/locale";
import { buildMachineTimelineSvg } from "./machineTimelineSvg";
import {
  fmtClock,
  fmtDateHr,
  fmtDayClock,
  fmtDec,
  fmtEur,
  fmtInt,
  fmtKg,
  fmtPct,
  fmtSignedEur,
  fmtSignedInt,
  fmtSignedPct,
} from "./reportFormat";

/**
 * A4 dokument dnevnog izvještaja za ispis / "Spremi kao PDF".
 *
 * Gradi se kao samostalni HTML string koji se upisuje u novi prozor (isti
 * pristup kao radni nalozi), pa nosi vlastiti CSS i ne oslanja se na stilove
 * aplikacije. Sve brojke dolaze gotove s backenda kroz `DailyProductionReport`
 * kako se ispis i ekran ne bi mogli razići.
 */

const esc = (value: unknown): string =>
  String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");

/** Klasa za predznak: zeleno kada je odstupanje u korist proizvodnje. */
const dc = (value: number, positiveIsGood = true): string => {
  if (value === 0) return "";
  const good = positiveIsGood ? value > 0 : value < 0;
  return good ? "pos" : "neg";
};

/** `class="num …"` za ćeliju odstupanja — jedan atribut, bez dupliranja. */
const numCls = (value: number, positiveIsGood = true): string => {
  const modifier = dc(value, positiveIsGood);
  return `class="num${modifier ? ` ${modifier}` : ""}"`;
};

const PRINT_CSS = `
  * { box-sizing: border-box; }
  body {
    font-family: "DejaVu Sans", Arial, Helvetica, sans-serif;
    color: #2c3e50; background: #f4f6f6; margin: 0; padding: 16px;
    font-size: 11px; -webkit-print-color-adjust: exact; print-color-adjust: exact;
  }
  .rep-page {
    width: 210mm; min-height: 297mm; margin: 0 auto 16px; background: #fff;
    padding: 10mm; box-shadow: 0 1px 6px rgba(0,0,0,0.18);
  }
  .rep-header {
    display: flex; justify-content: space-between; align-items: flex-end;
    border-bottom: 2px solid #1b2631; padding-bottom: 6px;
  }
  .rep-header h1 { font-size: 15px; margin: 0; color: #1b2631; }
  .rep-header .rep-date { font-size: 14px; font-weight: bold; text-align: right; }
  .rep-subhead {
    display: flex; justify-content: space-between; gap: 12px;
    font-size: 9px; color: #566573; padding: 4px 0 10px;
  }
  .rep-subhead .right { text-align: right; }
  h2.rep-section {
    font-size: 10px; font-weight: bold; color: #1b2631; text-transform: uppercase;
    letter-spacing: 0.4px; margin: 14px 0 5px;
  }
  table.rep-table { width: 100%; border-collapse: collapse; font-size: 9px; }
  table.rep-table th {
    background: #2c3e50; color: #fff; border: 0.5px solid #d5d8dc;
    padding: 4px 5px; text-align: center; font-weight: bold; font-size: 8.5px;
  }
  table.rep-table td {
    border: 0.5px solid #d5d8dc; padding: 3px 5px; vertical-align: middle;
  }
  table.rep-table tbody tr:nth-child(even) { background: #f8f9f9; }
  table.rep-table td.num { text-align: right; font-variant-numeric: tabular-nums; white-space: nowrap; }
  table.rep-table td.c { text-align: center; }
  table.rep-table tr.total td { background: #eaf2f8; font-weight: bold; }
  table.rep-table tr.sf td { color: #7f8c8d; font-style: italic; }
  td.pos, .pos { color: #1e8449; font-weight: bold; }
  td.neg, .neg { color: #c0392b; font-weight: bold; }
  .rep-banner { display: flex; gap: 0; margin-top: 6px; }
  .rep-banner .cell {
    flex: 1; background: #eafaf1; border: 0.75px solid #a9dfbf; text-align: center;
    padding: 6px 4px;
  }
  .rep-banner .cell + .cell { border-left: none; }
  .rep-banner .cell .label {
    font-size: 8px; font-weight: bold; color: #1b2631; text-transform: uppercase;
  }
  .rep-banner .cell .value { font-size: 15px; font-weight: bold; color: #1e8449; margin: 3px 0 2px; }
  .rep-banner .cell .value.muted { color: #566573; }
  .rep-banner .cell .sub { font-size: 8px; color: #566573; line-height: 1.4; }
  .rep-chart { margin-top: 4px; border: 0.5px solid #e5e7e9; padding: 4px; }
  .rep-note {
    margin-top: 10px; padding-top: 6px; border-top: 0.5px solid #d5d8dc;
    font-size: 8px; color: #7f8c8d;
  }
  .rep-empty { font-size: 9px; font-style: italic; color: #7f8c8d; padding: 6px 2px; }
  @media print {
    body { background: #fff; padding: 0; }
    .rep-page {
      width: auto; min-height: auto; margin: 0; padding: 0;
      box-shadow: none; page-break-after: always;
    }
    .rep-page:last-child { page-break-after: avoid; }
    /* Operativni plan je dulji od jedne stranice; zaglavlje se ponavlja. */
    table.rep-table thead { display: table-header-group; }
    table.rep-table tr { page-break-inside: avoid; }
  }
  @page { size: A4; margin: 10mm; }
`;

const renderKpiTable = (report: DailyProductionReport): string => {
  const plan = report.plan_totals;
  const real = report.real_totals;

  const diffBatches = real.batches_cnt - plan.batches_cnt;
  const diffPieces = real.packed_pieces - plan.pieces;
  const diffVpc = real.vpc_value - plan.vpc_value;
  const diffCost = real.ingredient_cost - plan.ingredient_cost;
  const diffYield = real.yield_pct - 100;

  const batchPct = plan.batches_cnt > 0 ? (real.batches_cnt / plan.batches_cnt) * 100 : 0;
  const piecesPct = plan.pieces > 0 ? (real.packed_pieces / plan.pieces) * 100 : 0;

  const rows = [
    [
      `<b>${t("Broj zamjesa (šarži)")}</b>`,
      `<b>${fmtInt(plan.batches_cnt)}</b>`,
      `<b>${fmtInt(real.batches_cnt)}</b>`,
      `<b>${fmtSignedInt(diffBatches)}</b> (${fmtDec(batchPct, 0)} %)`,
      dc(diffBatches),
    ],
    [
      t("Gotovi komadi (zapakirano)"),
      `${fmtInt(plan.pieces)} ${t("kom")}`,
      `${fmtInt(real.packed_pieces)} ${t("kom")}`,
      `${fmtSignedInt(diffPieces)} ${t("kom")} (${fmtDec(piecesPct, 1)} %)`,
      dc(diffPieces),
    ],
    [
      t("Evidentirani škart"),
      t("0 kom (0,0 %)"),
      `${fmtInt(real.scrap_pieces)} ${t("kom")} (${fmtDec(real.scrap_pct, 1)} %)`,
      `+${fmtInt(real.scrap_pieces)} ${t("kom")}`,
      real.scrap_pieces > 0 ? "neg" : "",
    ],
    [
      t("Yield (iskorištenje)"),
      fmtPct(100),
      `<b>${fmtPct(real.yield_pct)}</b>`,
      `<b>${fmtSignedPct(diffYield)}</b>`,
      dc(diffYield),
    ],
    [
      t("VPC vrijednost zapakirano"),
      fmtEur(plan.vpc_value),
      fmtEur(real.vpc_value),
      fmtSignedEur(diffVpc),
      dc(diffVpc),
    ],
    [
      t("Trošak primarnih sirovina"),
      fmtEur(plan.ingredient_cost),
      fmtEur(real.ingredient_cost),
      fmtSignedEur(diffCost),
      dc(diffCost, false),
    ],
    [t("Trošak pakiranja"), "—", "—", t("— (priprema u bazi)"), ""],
    [t("Trošak rada (osoblje)"), "—", "—", t("— (priprema u bazi)"), ""],
  ];

  return `
<table class="rep-table">
  <thead>
    <tr>
      <th style="width:34%">${t("Ključna metrika (KPI)")}</th>
      <th style="width:22%">${t("Planirano")}</th>
      <th style="width:22%">${t("Ostvareno")}</th>
      <th style="width:22%">${t("Odstupanje (razlika)")}</th>
    </tr>
  </thead>
  <tbody>
    ${rows
      .map(
        ([label, planCell, realCell, diffCell, diffAttr]) =>
          `<tr><td>${label}</td><td class="num">${planCell}</td>` +
          `<td class="num">${realCell}</td>` +
          `<td class="num${diffAttr ? ` ${diffAttr}` : ""}">${diffCell}</td></tr>`
      )
      .join("")}
  </tbody>
</table>`;
};

const renderBanner = (report: DailyProductionReport): string => {
  const plan = report.plan_totals;
  const real = report.real_totals;
  const diffMargin = real.margin - plan.margin;
  const diffMarginPct = real.margin_pct - plan.margin_pct;
  const scrapClass = real.scrap_pct > 0 ? "neg" : "pos";

  return `
<div class="rep-banner">
  <div class="cell">
    <div class="label">${t("Bruto marža")}</div>
    <div class="value">${fmtEur(real.margin)}</div>
    <div class="sub">${t("Plan:")} ${fmtEur(plan.margin)}<br/>
      <span class="${diffMargin >= 0 ? "pos" : "neg"}">${t("Razlika:")} ${fmtSignedEur(diffMargin)}</span></div>
  </div>
  <div class="cell">
    <div class="label">${t("Udio škarta")}</div>
    <div class="value ${scrapClass}">${fmtPct(real.scrap_pct)}</div>
    <div class="sub">${t("Plan: 0,0 %")}<br/>
      <span class="${scrapClass}">${t("Razlika:")} +${fmtDec(real.scrap_pct, 1)} %</span></div>
  </div>
  <div class="cell">
    <div class="label">${t("Marža / VPC zap.")}</div>
    <div class="value">${fmtPct(real.margin_pct)}</div>
    <div class="sub">${t("Plan:")} ${fmtPct(plan.margin_pct)}<br/>
      <span class="${diffMarginPct >= 0 ? "pos" : "neg"}">${t("Razlika:")} ${fmtSignedPct(diffMarginPct)}</span></div>
  </div>
  <div class="cell">
    <div class="label">${t("Rad / VPC proiz.")}</div>
    <div class="value muted">—</div>
    <div class="sub">${t("Plan: —")}<br/>${t("Razlika: —")}</div>
  </div>
</div>`;
};

const renderComparisonTable = (report: DailyProductionReport): string => {
  const plan = report.plan_totals;
  const real = report.real_totals;
  const diffVpc = real.vpc_value - plan.vpc_value;

  if (report.comparison.length === 0) {
    return `<p class="rep-empty">${t("Nema podataka za sučeljavanje po proizvodu.")}</p>`;
  }

  const rows = report.comparison
    .map((row) => {
      const scrapCell =
        row.scrap_pct == null ? "-" : `${fmtInt(row.scrap_pieces)} (${fmtDec(row.scrap_pct, 1)} %)`;
      const yieldCell = row.yield_pct == null ? "-" : fmtPct(row.yield_pct);
      return `<tr>
        <td>${esc(row.product_name)}</td>
        <td class="num">${fmtInt(row.plan_batches)} / ${fmtInt(row.real_batches)}</td>
        <td class="num">${fmtInt(row.plan_pieces)} / ${fmtInt(row.real_pieces)}</td>
        <td class="num">${scrapCell}</td>
        <td class="num">${yieldCell}</td>
        <td class="num">${fmtEur(row.plan_vpc)}</td>
        <td class="num">${fmtEur(row.real_vpc)}</td>
        <td ${numCls(row.diff_vpc)}>${fmtSignedEur(row.diff_vpc)}</td>
      </tr>`;
    })
    .join("");

  return `
<table class="rep-table">
  <thead>
    <tr>
      <th style="width:28%">${t("Proizvod")}</th>
      <th>${t("Šarži (Pl/Os)")}</th>
      <th>${t("Komada (Pl/Os)")}</th>
      <th>${t("Škart kom (%)")}</th>
      <th>${t("Yield (%)")}</th>
      <th>${t("Plan VPC (€)")}</th>
      <th>${t("VPC zapakirano (€)")}</th>
      <th>${t("Razlika VPC (€)")}</th>
    </tr>
  </thead>
  <tbody>${rows}</tbody>
  <tfoot>
    <tr class="total">
      <td>${t("UKUPNO ZA DAN")}</td>
      <td class="num">${fmtInt(plan.batches_cnt)} / ${fmtInt(real.batches_cnt)}</td>
      <td class="num">${fmtInt(plan.pieces)} / ${fmtInt(real.packed_pieces)}</td>
      <td class="num">${fmtInt(real.scrap_pieces)} (${fmtDec(real.scrap_pct, 1)} %)</td>
      <td class="num">${fmtPct(real.yield_pct)}</td>
      <td class="num">${fmtEur(plan.vpc_value)}</td>
      <td class="num">${fmtEur(real.vpc_value)}</td>
      <td ${numCls(diffVpc)}>${fmtSignedEur(diffVpc)}</td>
    </tr>
  </tfoot>
</table>`;
};

const renderOperationalPlan = (report: DailyProductionReport): string => {
  if (report.planned_batches.length === 0) {
    return `<p class="rep-empty">${t("Nema planiranih zamjesa za ovaj datum.")}</p>`;
  }

  const rows = report.planned_batches
    .map(
      (batch) => `<tr>
        <td class="c">${batch.sequence}</td>
        <td>${esc(batch.mix_code)}</td>
        <td>${esc(batch.product_name)}</td>
        <td class="num">${fmtKg(batch.final_mass_kg)}</td>
        <td>${esc(batch.mixer)}</td>
        <td class="c">${fmtClock(batch.mixing_start)} - ${fmtClock(batch.mixing_end)}</td>
        <td>${esc(batch.oven)}</td>
        <td class="c">${fmtClock(batch.baking_start)} - ${fmtClock(batch.baking_end)}</td>
      </tr>`
    )
    .join("");

  return `
<table class="rep-table">
  <thead>
    <tr>
      <th>#</th><th style="width:26%">${t("Šarža / MixCode")}</th><th style="width:20%">${t("Proizvod")}</th>
      <th>${t("Plan masa")}</th><th>${t("Mikser")}</th><th>${t("Zamjes")}</th><th>${t("Peć")}</th><th>${t("Pečenje")}</th>
    </tr>
  </thead>
  <tbody>${rows}</tbody>
</table>`;
};

const renderFinancialPlan = (report: DailyProductionReport): string => {
  const finished = report.plan_grouped.filter((group) => !group.is_semi_finished);
  const semiFinished = report.plan_grouped.filter((group) => group.is_semi_finished);
  const totals = report.plan_totals;

  if (report.plan_grouped.length === 0) {
    return `<p class="rep-empty">${t("Nema planiranih zamjesa za ovaj datum.")}</p>`;
  }

  const rows = finished
    .map(
      (group) => `<tr>
        <td>${esc(group.product_name)}</td>
        <td class="num">${fmtInt(group.batches_cnt)}</td>
        <td class="num">${fmtKg(group.total_mass_kg)}</td>
        <td class="num">${group.total_pieces > 0 ? fmtInt(group.total_pieces) : "-"}</td>
        <td class="num">${fmtEur(group.total_ingredient_cost)}</td>
        <td class="num">${group.cost_per_piece > 0 ? fmtEur(group.cost_per_piece) : "-"}</td>
        <td class="num">${group.vpc_unit_price > 0 ? fmtEur(group.vpc_unit_price) : "-"}</td>
        <td class="num">${fmtEur(group.total_vpc_value)}</td>
        <td ${numCls(group.margin_eur)}>${fmtEur(group.margin_eur)}</td>
      </tr>`
    )
    .join("");

  // Poluproizvodi su informativni: trošak im je već u gotovim proizvodima.
  const sfRows = semiFinished
    .map(
      (group) => `<tr class="sf">
        <td>${t("[poluproizvod]")} ${esc(group.product_name)}</td>
        <td class="num">${fmtInt(group.batches_cnt)}</td>
        <td class="num">${fmtKg(group.total_mass_kg)}</td>
        <td class="num">-</td>
        <td class="num">${fmtEur(group.total_ingredient_cost)}</td>
        <td class="num">-</td><td class="num">-</td><td class="num">-</td><td class="num">-</td>
      </tr>`
    )
    .join("");

  return `
<table class="rep-table">
  <thead>
    <tr>
      <th style="width:24%">${t("Proizvod")}</th><th>${t("Šarži")}</th><th>${t("Masa")}</th><th>${t("Komada")}</th>
      <th>${t("Trošak sirovina")}</th><th>${t("Sirovine/kom")}</th><th>${t("VPC/kom")}</th><th>${t("VPC ukupno")}</th><th>${t("Marža")}</th>
    </tr>
  </thead>
  <tbody>${rows}${sfRows}</tbody>
  <tfoot>
    <tr class="total">
      <td>${t("PLANIRANO UKUPNO ZA DAN")}</td>
      <td class="num">${fmtInt(totals.batches_cnt)}</td>
      <td class="num">${fmtKg(totals.mass_kg)}</td>
      <td class="num">${fmtInt(totals.pieces)}</td>
      <td class="num">${fmtEur(totals.ingredient_cost)}</td>
      <td class="num"></td><td class="num"></td>
      <td class="num">${fmtEur(totals.vpc_value)}</td>
      <td class="num">${fmtEur(totals.margin)} (${fmtDec(totals.margin_pct, 1)} %)</td>
    </tr>
  </tfoot>
</table>`;
};

const renderRealized = (report: DailyProductionReport): string => {
  if (report.realized_grouped.length === 0) {
    return `<p class="rep-empty">${t("Nema evidentiranih ostvarenih šarži za ovaj datum.")}</p>`;
  }

  const totals = report.real_totals;
  const rows = report.realized_grouped
    .map(
      (group) => `<tr>
        <td>${esc(group.product_name)}</td>
        <td class="num">${fmtInt(group.batches_cnt)}</td>
        <td class="num">${fmtKg(group.total_mass_kg)}</td>
        <td class="num">${fmtInt(group.packed_pieces)}</td>
        <td class="num">${fmtInt(group.scrap_pieces)} (${fmtDec(group.scrap_pct, 1)} %)</td>
        <td class="num">${fmtEur(group.actual_ingredient_cost)}</td>
        <td class="num">${fmtEur(group.total_vpc_value)}</td>
        <td ${numCls(group.margin_eur)}>${fmtEur(group.margin_eur)}</td>
      </tr>`
    )
    .join("");

  return `
<table class="rep-table">
  <thead>
    <tr>
      <th style="width:26%">${t("Proizvod")}</th><th>${t("Šarži")}</th><th>${t("Masa")}</th><th>${t("Zapakirano")}</th>
      <th>${t("Škart (kom / %)")}</th><th>${t("Stvarni trošak")}</th><th>${t("Ostvareni VPC")}</th><th>${t("Marža")}</th>
    </tr>
  </thead>
  <tbody>${rows}</tbody>
  <tfoot>
    <tr class="total">
      <td>${t("OSTVARENO UKUPNO ZA DAN")}</td>
      <td class="num">${fmtInt(totals.batches_cnt)}</td>
      <td class="num">${fmtKg(totals.mass_kg)}</td>
      <td class="num">${fmtInt(totals.packed_pieces)}</td>
      <td class="num">${fmtInt(totals.scrap_pieces)} (${fmtDec(totals.scrap_pct, 1)} %)</td>
      <td class="num">${fmtEur(totals.ingredient_cost)}</td>
      <td class="num">${fmtEur(totals.vpc_value)}</td>
      <td class="num">${fmtEur(totals.margin)} (${fmtDec(totals.margin_pct, 1)} %)</td>
    </tr>
  </tfoot>
</table>`;
};

/**
 * Vraća cijeli HTML dokument izvještaja. Prva stranica je sažetak (KPI, marža,
 * timeline, sučeljavanje), druga plan, treća ostvarenje.
 */
export const buildDailyReportDocument = (report: DailyProductionReport): string => {
  const timeline = report.final_timeline;
  const timelineLabel = timeline ? `TL-${timeline.id} (v${timeline.version})` : "N/A";
  const confirmedLabel = timeline?.confirmed_at ? fmtDayClock(timeline.confirmed_at) : t("Nepotvrđen");
  const chart = buildMachineTimelineSvg(report.machine_timeline, { theme: "print", width: 760 });

  const header = `
<div class="rep-header">
  <h1>BOOGIE BAKERY — ${t("Dnevni proizvodni izvještaj")}</h1>
  <div class="rep-date">${t("Datum:")} ${fmtDateHr(report.target_date)}</div>
</div>
<div class="rep-subhead">
  <div>${t("Konačni plan:")} <b>${esc(timelineLabel)}</b> &nbsp;|&nbsp; ${t("Potvrđen:")} <b>${esc(
    confirmedLabel
  )}</b> &nbsp;|&nbsp; ${t("Start rada:")} <b>${fmtClock(report.prod_start)}</b></div>
  <div class="right">${t("Promjene naloga:")} <b>${report.changes_before_start}</b> ${t("prije starta")} &nbsp;|&nbsp;
    <b>${report.changes_during_prod}</b> real-time</div>
</div>`;

  // `generated_at` je UTC ISO s backenda; footer nosi lokalno vrijeme pogona.
  const generatedLocal = new Date(report.generated_at).toLocaleString(dateLocale(), {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });

  const footer = `
<p class="rep-note">
  ${t("Izvještaj generiran automatski iz MES baze {when}.", { when: generatedLocal })}
  ${t("Troškovi pakiranja i rada označeni su s \"—\" te će biti uključeni nakon unosa u bazu.")}
</p>`;

  return `<!DOCTYPE html>
<html lang="${dateLocale().startsWith("en") ? "en" : "hr"}">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>${t("Dnevni proizvodni izvještaj")} — ${fmtDateHr(report.target_date)}</title>
  <style>${PRINT_CSS}</style>
</head>
<body>
  <div class="rep-page">
    ${header}
    <h2 class="rep-section">${t("Glavne proizvodne metrike (plan vs. ostvareno)")}</h2>
    ${renderKpiTable(report)}
    ${renderBanner(report)}
    <h2 class="rep-section">${t("Strojevi — radni timeline (planirano vs. ostvareno)")}</h2>
    ${
      chart
        ? `<div class="rep-chart">${chart}</div>`
        : `<p class="rep-empty">${t("Nema unesenih satnica za prikaz timelinea na strojevima za ovaj datum.")}</p>`
    }
    <h2 class="rep-section">${t("Detaljno sučeljavanje po proizvodu (plan vs. ostvareno)")}</h2>
    ${renderComparisonTable(report)}
    ${footer}
  </div>

  <div class="rep-page">
    ${header}
    <h2 class="rep-section">${t("Operativni plan rasporeda na strojevima")}${
      timeline ? ` (konačni plan: ${esc(timelineLabel)})` : ""
    }</h2>
    ${renderOperationalPlan(report)}
    <h2 class="rep-section">${t("Financijski plan (grupirano po proizvodu)")}</h2>
    ${renderFinancialPlan(report)}
    ${footer}
  </div>

  <div class="rep-page">
    ${header}
    <h2 class="rep-section">${t("Financijsko ostvareno (zabilježenih šarži: {count})", { count: report.realized_batches.length })}</h2>
    ${renderRealized(report)}
    ${footer}
  </div>

  <script>
    window.addEventListener("load", function () {
      setTimeout(function () { window.print(); }, 250);
    });
  </script>
</body>
</html>`;
};

export default buildDailyReportDocument;
